import React, { useMemo, useRef } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDevice } from '../../hooks/useDevice';
import { useDeviceListNeighbors } from '../../hooks/useDeviceListNeighbors';
import { isLocal } from '../../utils/isLocal';
import { deviceClipLabel } from '../../utils/clipLabel';
import {
  deviceListHref,
  readDeviceListContext,
  stepDeviceListContext,
  workspaceHref,
} from '../../utils/deviceListContext';
import { isValidTab, readStickyTab, WORKSPACE_TABS, writeStickyTab } from './tabConfig';
import { WorkspaceContext } from './workspaceContext';
import DecommissionedBadge from '../DecommissionedBadge';
import Button, { buttonClass } from '../ui/Button';
import { Tabs, TabsList, TabsTrigger, isModifiedLinkEvent, useLinkedTabPointerSelection } from '../pilot-ui/tabs';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { DeviceSearchRow } from '../../types';

/**
 * Plan 94 - Device Workspace shell.
 *
 * Resolves the route `:deviceId` to a Device via GET /devices/:id (scope-checked),
 * renders the identity/app-context header, a permission-gated tab bar, a close
 * control back to the device list, and an <Outlet/> for the active tab. Device
 * identity lives in the URL; only the selected tab is a persisted preference.
 *
 * FS-542: when the workspace was opened from the Devices page, that list is
 * carried on the URL (see utils/deviceListContext). The back arrow and the
 * close control return to it as it was, and Previous/Next step through it
 * without leaving the tab being worked in.
 *
 * FS-634: Escape is a third way to the same place, so a search typed on the
 * Devices page, opened with Enter (FS-633) and left with Escape never needs
 * the mouse.
 */
const DeviceWorkspaceLayout: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { hasPermission } = useAuth();
  const { device, loading, notFound, error, refetch } = useDevice(deviceId);

  const navigate = useNavigate();
  const location = useLocation();
  const listCtx = readDeviceListContext(new URLSearchParams(location.search));
  const listHref = deviceListHref(listCtx);
  const neighbors = useDeviceListNeighbors(listCtx);

  // The tab is the child route's param, so read it off the path rather than
  // this route's params. An unknown tab is about to be redirected away; fall
  // back to the sticky preference so a step doesn't carry the bad key along.
  const tabMatch = useMatch('/devices/:deviceId/:tab');
  const routeTab = tabMatch?.params.tab;
  const tab = isValidTab(routeTab) ? (routeTab as string) : readStickyTab();

  const visibleTabs = useMemo(
    () => WORKSPACE_TABS.filter((t) => !t.perm || hasPermission(t.perm)),
    [hasPermission],
  );
  // Base UI activates a focused tab before click. Keep modified anchor clicks
  // as browser links even when that pointer press also moved focus.

  // Escape leaves the workspace when no field or overlay owns it. The shared
  // resolver keeps this page action behind the active layer.
  const listHrefRef = useRef(listHref);
  const linkedTabs = useLinkedTabPointerSelection();
  listHrefRef.current = listHref;

  useEscapeKey(
    () => navigate(listHrefRef.current),
    !!device,
    (event) => !ownsEscape(event.target),
  );

  if (loading) {
    return <ShellMessage>Loading device…</ShellMessage>;
  }

  if (error) {
    return <ShellMessage tone="error">{error}</ShellMessage>;
  }

  if (notFound || !device) {
    // Device list is scope-filtered server-side, so an unresolved id is either a
    // bad link or a device outside the operator's scope — same outcome either way.
    return (
      <ShellMessage tone="error">
        <p className="font-semibold">Device not found</p>
        <p className="mt-1 text-sm">
          No device with id <code className="font-mono">{deviceId}</code> is in your scope.
        </p>
        <Link to={listHref} className={`${buttonClass('link')} mt-3 inline-block`} title="Return to the device list">
          Back to all devices
        </Link>
      </ShellMessage>
    );
  }

  const appCtx = device.app_context;
  // FS-825 — the description is the owner's name, the same on every Clip in
  // the home, so the Clip's filter name (or its slot) follows it.
  const heading = deviceClipLabel(device);

  // Previous/Next appear only when a carried index still points at the device
  // on screen. While the window for a new position is in flight there is
  // nothing to check it against yet, so the controls stay in place (disabled)
  // instead of flickering out and back on every step; if the list really has
  // moved under the walk, they go away rather than stepping somewhere
  // arbitrary. A device opened without list context never has them.
  const stepping =
    !!listCtx && !neighbors.error && (neighbors.loading || neighbors.current?.id === device.id);

  const stepTo = (row: DeviceSearchRow | null, index: number) => {
    if (!row || !listCtx) return;
    navigate(workspaceHref(row.id, tab, stepDeviceListContext(listCtx, index)));
  };

  return (
    <div className="h-full w-full bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-white text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-2">
        {/* Header */}
        <div className="bg-card border border-border rounded-lg shadow-md p-5">
          {/* Keep navigation above the identity block until there is room for
              enlarged text and the device controls side by side. */}
          <div className="flex flex-nowrap flex-col-reverse gap-4 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {/* Back — the first thing in the header, in the conventional
                  place, returning to the list this device was opened from
                  with its filters, sort, page size and page intact. */}
              <Link
                to={listHref}
                aria-label="Back to device list"
                title={listCtx?.query ? 'Back to device list (same filters)' : 'Back to device list'}
                className={`${buttonClass('icon', 'md')} shrink-0 mt-1`}
              >
                <ArrowLeft className="h-6 w-6" />
              </Link>

              <div className="min-w-0">
                {/* Let the name wrap before it can obscure the device id. */}
                <div className="flex flex-wrap items-baseline gap-2">
                  <h2 className="min-w-0 wrap-break-word text-2xl font-bold" title={heading}>
                    {heading}
                  </h2>
                  <span className="shrink-0 text-sm text-muted-foreground font-mono">#{device.id}</span>
                  <DecommissionedBadge
                    decommissioned={device.decommissioned}
                    className="shrink-0"
                  />
                </div>
                <div className="mt-1 text-sm text-gray-600 font-mono break-all">{device.name}</div>

                <dl className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  <div className="flex min-w-0 gap-2">
                    <dt className="text-muted-foreground shrink-0">Responsibility</dt>
                    <dd className="min-w-0 font-mono break-all">{device.entity_path || '—'}</dd>
                  </div>
                  <div className="flex min-w-0 gap-2">
                    <dt className="text-muted-foreground shrink-0">MyFluxHive</dt>
                    <dd className="min-w-0 wrap-break-word">
                      {!appCtx || appCtx.status === 'missing' ? (
                        <span className="text-muted-foreground">Not registered in app</span>
                      ) : (
                        <span>
                          {appCtx.owner_email || appCtx.owner_name || 'Unknown owner'}
                          {appCtx.owner_email && appCtx.owner_name && <span className="text-muted-foreground"> · {appCtx.owner_name}</span>}
                          {appCtx.location_name && <span className="text-muted-foreground"> · {appCtx.location_name}</span>}
                          {appCtx.hvac_name && <span className="text-muted-foreground"> · {appCtx.hvac_name}</span>}
                          {appCtx.status === 'ambiguous' && (
                            <span className="ml-1 text-status-warning-foreground" title="Duplicate app claims">⚠</span>
                          )}
                        </span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2">
              {stepping && listCtx && (
                <div
                  className="flex items-center gap-1"
                  role="group"
                  aria-label="Step through the device list"
                >
                  <StepButton
                    label="Previous device in list"
                    disabled={!neighbors.prev}
                    onClick={() => stepTo(neighbors.prev, listCtx.index - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    Prev
                  </StepButton>
                  <span className="px-1 text-sm text-gray-600 whitespace-nowrap tabular-nums">
                    Device {listCtx.index + 1} of {neighbors.total || '…'}
                  </span>
                  <StepButton
                    label="Next device in list"
                    disabled={!neighbors.next}
                    onClick={() => stepTo(neighbors.next, listCtx.index + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </StepButton>
                </div>
              )}

              {/* Close — the same destination as the back arrow. Both are
                  deliberate: the pairing the mobile app settled on. */}
              <Link
                to={listHref}
                aria-label="Close device view"
                title="Close — back to device list"
                className={`${buttonClass('icon', 'md')} shrink-0 -mt-1 -mr-1`}
              >
                <X className="h-6 w-6" />
              </Link>
            </div>
          </div>

          {/* Tab bar */}
          <Tabs value={tab} onValueChange={(value, details) => {
            if (linkedTabs.shouldCancelSelection(details.event)) {
              details.cancel();
              return;
            }
            writeStickyTab(value);
            navigate(workspaceHref(device.id, value, listCtx));
          }} className="mt-5">
            <TabsList variant="line" activateOnFocus aria-label="Device workspace" className="flex w-full flex-wrap justify-start border-b border-border">
              {visibleTabs.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  render={<Link to={workspaceHref(device.id, t.key, listCtx)} />}
                  nativeButton={false}
                  onPointerDown={linkedTabs.onPointerDown}
                  onClick={(event) => {
                    if (!isModifiedLinkEvent(event)) {
                      event.preventDefault();
                    }
                  }}
                  className="h-9 flex-none px-3"
                  title={`Open ${t.label} for this device`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Active tab */}
        <div className="mt-2">
          <Outlet context={{ device, refetchDevice: refetch } satisfies WorkspaceContext} />
        </div>
      </div>
    </div>
  );
};

/** Input types holding no text, so Escape has nothing to cancel in them. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'image',
  'file',
  'range',
  'color',
]);

/**
 * FS-634 — true when something nearer than the page should have this Escape:
 * an open dialog, modal or popover anywhere on screen, or a field the key
 * cancels or clears. Evaluated at keydown time so it reflects what is open
 * now, not what was open when the listener was registered.
 */
const ownsEscape = (target: EventTarget | null): boolean => {
  if (document.querySelector('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')) {
    return true;
  }
  const el = target as HTMLElement | null;
  // Rules out `document` and `window` as targets before touching element APIs.
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  // `isContentEditable` is not implemented in every environment the suite
  // runs in, so the attribute is checked too — and an explicit
  // contenteditable="false" is not an editor.
  const editable = el.closest('[contenteditable]');
  if (editable && editable.getAttribute('contenteditable') !== 'false') return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag === 'input') {
    const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(type);
  }
  return false;
};

/** One step control. Disabled at the ends of the list — the walk never wraps. */
const StepButton: React.FC<{
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, disabled, onClick, children }) => (
  <Button variant="secondary" onClick={onClick} disabled={disabled} aria-label={label} title={label}>
    {children}
  </Button>
);

const ShellMessage: React.FC<{ children: React.ReactNode; tone?: 'info' | 'error' }> = ({
  children,
  tone = 'info',
}) => (
  <div className="max-w-7xl mx-auto p-2">
    <div
      className={`mt-8 p-6 rounded-lg border border-border bg-card shadow-md ${
        tone === 'error' ? 'text-status-error-foreground' : 'text-gray-600'
      }`}
    >
      {children}
    </div>
  </div>
);

export default DeviceWorkspaceLayout;
